import { DataListCard, type DataListColumn, type DataListAction } from '@/components/common/DataListCard';
import { useJobsByLead } from '../hooks/useJobsByLead';

interface LeadJobsPanelProps {
  leadId: number;
}

interface JobSummary {
  id: number;
  jobNumber: string;
  status: string;
  startDate: string;
  totalCharges: number;
  profit: number;
}

export function LeadJobsPanel({ leadId }: LeadJobsPanelProps) {
  const { data: jobs, isLoading, error } = useJobsByLead(leadId);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const columns: DataListColumn<JobSummary>[] = [
    {
      key: 'jobNumber',
      label: 'Job Number',
      render: (job) => <span className="font-semibold">{job.jobNumber}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (job) => <span className="font-semibold">{job.status}</span>,
    },
    {
      key: 'startDate',
      label: 'Start Date',
      render: (job) => formatDate(job.startDate),
    },
    {
      key: 'totalCharges',
      label: 'Total Charges',
      render: (job) => <span className="font-semibold">{formatCurrency(job.totalCharges)}</span>,
    },
    {
      key: 'profit',
      label: 'Profit',
      render: (job) => (
        <span className={`font-semibold ${job.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {formatCurrency(job.profit)}
        </span>
      ),
    },
  ];

  const actions: DataListAction<JobSummary>[] = [
    {
      label: 'View',
      href: (job) => `/jobs/${job.id}`,
    },
  ];

  return (
    <div className="bg-white p-1.5 rounded-[20px] border border-[#d9d9d9]">
      <h3 className="text-[#0A0A9E] font-semibold text-sm mb-1">
        Jobs {jobs.length > 0 && `(${jobs.length})`}
      </h3>
      <DataListCard
        items={jobs}
        columns={columns}
        actions={actions}
        keyExtractor={(job) => job.id}
        isLoading={isLoading}
        loadingMessage="Loading jobs..."
        emptyMessage="No jobs created for this lead yet."
        error={error}
      />
    </div>
  );
}
